import { Component, OnInit } from '@angular/core';
import { UnitOfWorkService } from '../../../services/unit-of-work/unit-of-work.service';
import { Router, ActivatedRoute } from '@angular/router';
import { Observable } from 'rxjs';
import { ApplicationInsightsService } from '../../../services/application-insights/application-insights.service';
import { DialogHelper } from '../../../services/dialog-helper';
import { IsBusyService } from '../../../services/is-busy/is-busy.service';
import { GlobalErrorHandler } from '../../../services/global-error-handler';
import { NgbDateAdapter, NgbDateNativeAdapter } from '@ng-bootstrap/ng-bootstrap';
import * as model from '../../../../model/model';

const LOADING_KEY: string = 'inspection-engagement-editor:loadData';
const SAVING_KEY: string = 'inspection-engagement-editor:save';
const PROJECT_PAGE_SIZE: number = 50;
const PROJECT_SEARCH_TIMEOUT: number = 300;
const APT_CAW_PAGE_SIZE: number = 50;
const APT_CAW_SEARCH_TIMEOUT: number = 300;
const CLIENT_PAGE_SIZE: number = 50;
const CLIENT_SEARCH_TIMEOUT: number = 300;
const CLIENT_MIN_SEARCH_LENGTH: number = 3;

@Component({
    selector: 'app-inspection-engagement-editor',
    templateUrl: './inspection-engagement-editor.component.html',
    providers: [{ provide: NgbDateAdapter, useClass: NgbDateNativeAdapter }],
    standalone: false
})
export class InspectionEngagementEditorComponent implements OnInit {
    isLoading: boolean = false;

    engagement: model.InspectionEngagement = null;

    clients: model.Client[] = [];
    clientsCount: number = 0;
    clientsPage: number = 1;
    clientName: string = null;
    private activeClientQuery: string = null;
    clientsLoading: boolean = false;
    clientSearchTimer: any = null;
    private clientRequestVersion: number = 0;
    projects: model.Project[] = [];
    availableProjects: model.Project[] = [];
    selectedProject: model.Project = null;
    projectsCount: number = 0;
    projectsPage: number = 1;
    projectName: string = null;
    projectsLoading: boolean = false;
    projectSearchTimer: any = null;
    private projectRequestVersion: number = 0;
    aptCaws: model.AptCaw[] = [];
    availableAptCaws: model.AptCaw[] = [];
    selectedAptCaw: model.AptCaw = null;
    aptCawsCount: number = 0;
    aptCawsPage: number = 1;
    aptCawName: string = null;
    aptCawsLoading: boolean = false;
    aptCawSearchTimer: any = null;
    private aptCawRequestVersion: number = 0;
    industries: model.Industry[] = [];
    availableIndustries: model.Industry[] = [];
    selectedIndustry: model.Industry = null;
    offices: model.Office[] = [];
    businessLines: model.BusinessLine[] = [];

    employees: model.Employee[] = [];
    availableInspectionEmployees: model.Employee[] = [];
    selectedInspectionTeamId: string = null;
    availableEngagementEmployees: model.Employee[] = [];
    selectedEngagementTeamId: string = null;
    teamMemberRoles: model.InspectionTeamRole[] = [];
    private engagementTeamSyncVersion: number = 0;
    private engagementTeamSyncChain: Promise<any> = Promise.resolve();
    specialtyReviewerTypes: model.SpecialtyReviewerType[] = [];
    topics: model.Topic[] = [];
    standards: model.Standard[] = [];
    standardSelections: { [key: number]: number[] } = {};

    constructor(
        private uow: UnitOfWorkService,
        private appInsights: ApplicationInsightsService,
        private errorHandler: GlobalErrorHandler,
        private activeRoute: ActivatedRoute,
        public router: Router,
        public isBusy: IsBusyService,
        public dialogHelper: DialogHelper
    ) {
        appInsights.logPageView();
    }

    ngOnInit() {
        this.isLoading = true;
        this.isBusy.set(LOADING_KEY, true, 'Loading data');

        const id = this.activeRoute.snapshot.queryParams['id'];

        Promise.all([
            this.loadLookups(),
            this.loadEngagement(id)
        ])
        .then(() => {
            this.syncRegionFromOffice();
            this.setAvailableIndustries();
            const loads: Promise<any>[] = [this.loadClients()];
            if (this.engagement && this.engagement.clientId) {
                loads.push(this.loadProjects(this.engagement.clientId));
                loads.push(this.loadAptCaws(this.engagement.clientId));
            }
            return Promise.all(loads);
        })
        .catch(reason => {
            this.errorHandler.handleError(reason);
        })
        .finally(() => {
            this.isLoading = false;
            this.isBusy.set(LOADING_KEY, false);
        });
    }

    canDeactivate(): Observable<boolean> | boolean {
        if (this.uow.hasChanges.value) {
            const proceed = this.dialogHelper.confirm();

            proceed.subscribe(result => {
                if (result) {
                    this.uow.rollback();
                }
            });

            return proceed;
        }

        return true;
    }

    private loadLookups() {
        return Promise.all([
            this.uow.getOffices().then(result => { this.offices = result; }),
            this.uow.getBusinessLines().then(result => { this.businessLines = result; }),
            this.uow.getActiveIndustries().then(result => {
                this.industries = result;
                this.setAvailableIndustries();
            }),
            this.uow.getSpecialtyReviewerTypes().then(result => { this.specialtyReviewerTypes = result; }),
            this.uow.getInspectionTeamRoles().then(result => { this.teamMemberRoles = result; }),
            this.uow.getActiveTopics().then(result => { this.topics = result; }),
            this.uow.getActiveStandards().then(result => { this.standards = result; })
        ]);
    }

    private loadEngagement(id: string) {
        if (!id || id === 'new') {
            this.engagement = this.uow.inspectionEngagements.createEntity();
            this.engagement.inspectionYear = new Date().getFullYear();
            return Promise.resolve();
        }

        return this.uow.getInspectionEngagementById(parseInt(id))
            .then(result => {
                this.engagement = (result && result.length) ? result[0] : null;
                this.initStandardSelections();
            });
    }

    onOfficeChanged(officeId: string) {
        if (!this.engagement) {
            return;
        }

        this.engagement.officeId = officeId || null;
        if (!this.engagement.officeId) {
            this.engagement.office = null;
        }
        this.syncRegionFromOffice();
    }

    private syncRegionFromOffice() {
        if (!this.engagement) {
            return;
        }

        if (!this.engagement.officeId) {
            this.engagement.office = null;
            this.engagement.regionCode = null;
            return;
        }

        const office = this.offices.find(item => item.id === this.engagement.officeId)
            || (this.engagement.office?.id === this.engagement.officeId
                ? this.engagement.office
                : null);
        if (!office) {
            return;
        }

        this.engagement.office = office;
        this.engagement.regionCode = office.regionCode || null;
    }

    private initStandardSelections() {
        this.standardSelections = {};
        if (!this.engagement || !this.engagement.inspectionTeamMembers) {
            return;
        }

        this.engagement.inspectionTeamMembers
            .filter(m => !m.isDeleted)
            .forEach(member => {
                (member.inspectionEngagementTeamMemberTopics || [])
                    .filter(t => !t.isDeleted)
                    .forEach(topicRow => {
                        this.standardSelections[topicRow.id] = (topicRow.inspectionEngagementTeamMemberTopicStandards || [])
                            .filter(s => !s.isDeleted)
                            .map(s => s.standardId);
                    });
            });
    }

    // ----- Client -----

    loadClients() {
        clearTimeout(this.clientSearchTimer);
        this.clientName = null;
        this.clientsPage = 1;
        this.clientsLoading = true;
        const requestVersion = ++this.clientRequestVersion;

        return Promise.all([
            this.uow.getClientsCount(),
            this.uow.getClients(null, null, this.clientsPage, CLIENT_PAGE_SIZE)
        ])
            .then(([count, clients]) => {
                if (requestVersion === this.clientRequestVersion) {
                    this.activeClientQuery = null;
                    this.clientsCount = this.countValue(count);
                    this.clients = clients;
                    return this.ensureSelectedClientPresent(requestVersion);
                }
                return null;
            })
            .catch(reason => this.errorHandler.handleError(reason))
            .finally(() => {
                if (requestVersion === this.clientRequestVersion) {
                    this.clientsLoading = false;
                }
            });
    }

    onClientsSearch(event: { term: string }) {
        this.clientName = (event?.term || '').trim();
        clearTimeout(this.clientSearchTimer);
        this.clientSearchTimer = setTimeout(() => this.searchClients(), CLIENT_SEARCH_TIMEOUT);
    }

    private searchClients() {
        if (!this.clientName) {
            this.loadClients();
            return;
        }

        if (this.clientName.length < CLIENT_MIN_SEARCH_LENGTH) {
            return;
        }

        this.clientsPage = 1;
        this.clientsLoading = true;
        const searchTerm = this.clientName;
        const requestVersion = ++this.clientRequestVersion;
        Promise.all([
            this.uow.getClientsCount(null, searchTerm),
            this.uow.getClients(null, searchTerm, this.clientsPage, CLIENT_PAGE_SIZE)
        ])
            .then(([count, clients]) => {
                if (requestVersion === this.clientRequestVersion && searchTerm === this.clientName) {
                    this.activeClientQuery = searchTerm;
                    this.clientsCount = this.countValue(count);
                    this.clients = clients;
                    return this.ensureSelectedClientPresent(requestVersion);
                }
                return null;
            })
            .catch(reason => this.errorHandler.handleError(reason))
            .finally(() => {
                if (requestVersion === this.clientRequestVersion) {
                    this.clientsLoading = false;
                }
            });
    }

    onClientsScrollToEnd() {
        this.fetchMoreClients();
    }

    onClientsScroll(event: { end: number }) {
        if (!event || this.clientsLoading || this.clients.length >= this.clientsCount) {
            return;
        }

        if (event.end >= this.clients.length - 10) {
            this.fetchMoreClients();
        }
    }

    private fetchMoreClients() {
        if (this.clientsLoading || this.clients.length >= this.clientsCount) {
            return;
        }

        const requestVersion = this.clientRequestVersion;
        const nextPage = this.clientsPage + 1;
        this.clientsPage = nextPage;
        this.clientsLoading = true;
        this.uow.getClients(null, this.activeClientQuery, nextPage, CLIENT_PAGE_SIZE)
            .then(result => {
                if (requestVersion === this.clientRequestVersion) {
                    const knownClientIds = new Set(this.clients.map(client => client.id));
                    this.clients = this.clients.concat(result.filter(client => !knownClientIds.has(client.id)));
                }
            })
            .catch(reason => {
                if (requestVersion === this.clientRequestVersion && this.clientsPage === nextPage) {
                    this.clientsPage = nextPage - 1;
                }
                this.errorHandler.handleError(reason);
            })
            .finally(() => {
                if (requestVersion === this.clientRequestVersion) {
                    this.clientsLoading = false;
                }
            });
    }

    private ensureSelectedClientPresent(requestVersion: number) {
        const clientId = this.engagement?.clientId;
        if (!clientId || this.clients.some(client => client.id === clientId)) {
            return Promise.resolve();
        }

        return this.uow.getClients(clientId, null, 1, CLIENT_PAGE_SIZE)
            .then(result => {
                const selected = (result || []).find(client => client.id === clientId);
                if (selected
                    && requestVersion === this.clientRequestVersion
                    && !this.clients.some(client => client.id === clientId)) {
                    this.clients = [selected].concat(this.clients);
                }
            });
    }

    loadProjects(clientId: string) {
        clearTimeout(this.projectSearchTimer);
        this.projectName = null;
        const requestVersion = ++this.projectRequestVersion;

        if (!clientId) {
            this.projects = [];
            this.availableProjects = [];
            this.projectsCount = 0;
            return Promise.resolve();
        }

        this.projectsPage = 1;
        this.projectsLoading = true;
        return Promise.all([
            this.uow.getProjectsCount(null, null, clientId),
            this.uow.getProjects(null, null, clientId, null, this.projectsPage, PROJECT_PAGE_SIZE)
        ])
            .then(([count, projects]) => {
                if (requestVersion === this.projectRequestVersion && clientId === this.engagement?.clientId) {
                    this.projectsCount = this.countValue(count);
                    this.projects = projects;
                    this.setAvailableProjects();
                }
            })
            .catch(reason => this.errorHandler.handleError(reason))
            .finally(() => {
                if (requestVersion === this.projectRequestVersion) {
                    this.projectsLoading = false;
                }
            });
    }

    onClientChange() {
        (this.engagement.inspectionEngagementProjects || [])
            .filter(project => this.isActiveProject(project))
            .slice()
            .forEach(project => this.removeProjectEntity(project));
        this.clearEngagementTeam();
        this.selectedProject = null;
        this.assignedAptCaws.slice().forEach(aptCaw => this.removeAptCaw(aptCaw));
        this.selectedAptCaw = null;
        this.loadProjects(this.engagement.clientId);
        this.loadAptCaws(this.engagement.clientId);
    }

    private countValue(count: number | number[]) {
        return Array.isArray(count) ? (count[0] || 0) : (count || 0);
    }

    private setAvailableProjects() {
        const assignedProjectIds = new Set(
            (this.engagement?.inspectionEngagementProjects || [])
                .filter(project => this.isActiveProject(project))
                .map(project => project.projectId)
        );
        this.availableProjects = (this.projects || [])
            .filter(project => !assignedProjectIds.has(project.id));
    }

    onProjectsSearch(event: { term: string }) {
        this.projectName = (event?.term || '').trim();
        clearTimeout(this.projectSearchTimer);
        this.projectSearchTimer = setTimeout(() => this.searchProjects(), PROJECT_SEARCH_TIMEOUT);
    }

    private searchProjects() {
        if (!this.engagement?.clientId) {
            return;
        }

        if (!this.projectName) {
            this.loadProjects(this.engagement.clientId);
            return;
        }

        this.projectsPage = 1;
        this.projectsLoading = true;
        const searchTerm = this.projectName;
        const requestVersion = ++this.projectRequestVersion;
        const clientId = this.engagement.clientId;
        Promise.all([
            this.uow.getProjectsCount(null, searchTerm, clientId),
            this.uow.getProjects(null, searchTerm, clientId, null, this.projectsPage, PROJECT_PAGE_SIZE)
        ])
            .then(([count, projects]) => {
                if (requestVersion === this.projectRequestVersion
                    && searchTerm === this.projectName
                    && clientId === this.engagement?.clientId) {
                    this.projectsCount = this.countValue(count);
                    this.projects = projects;
                    this.setAvailableProjects();
                }
            })
            .catch(reason => this.errorHandler.handleError(reason))
            .finally(() => {
                if (requestVersion === this.projectRequestVersion) {
                    this.projectsLoading = false;
                }
            });
    }

    onProjectsScrollToEnd() {
        this.fetchMoreProjects();
    }

    onProjectsScroll(event: { end: number }) {
        if (!event || this.projectsLoading || this.projects.length >= this.projectsCount) {
            return;
        }

        if (event.end >= this.projects.length - 10) {
            this.fetchMoreProjects();
        }
    }

    private fetchMoreProjects() {
        if (this.projectsLoading || this.projects.length >= this.projectsCount || !this.engagement?.clientId) {
            return;
        }

        const requestVersion = this.projectRequestVersion;
        const clientId = this.engagement.clientId;
        const searchTerm = this.projectName;
        const nextPage = this.projectsPage + 1;
        this.projectsPage = nextPage;
        this.projectsLoading = true;
        this.uow.getProjects(null, searchTerm, clientId, null, nextPage, PROJECT_PAGE_SIZE)
            .then(result => {
                if (requestVersion === this.projectRequestVersion
                    && clientId === this.engagement?.clientId
                    && searchTerm === this.projectName) {
                    this.projects = this.projects.concat(result);
                    this.setAvailableProjects();
                }
            })
            .catch(reason => this.errorHandler.handleError(reason))
            .finally(() => {
                if (requestVersion === this.projectRequestVersion) {
                    this.projectsLoading = false;
                }
            });
    }

    private isActiveProject(project: model.InspectionEngagementProject) {
        return !project.isDeleted
            && !project.entityAspect.entityState.isDeleted()
            && !project.entityAspect.entityState.isDetached();
    }

    get assignedInspectionProjects() {
        return this.engagement && this.engagement.inspectionEngagementProjects
            ? this.engagement.inspectionEngagementProjects.filter(project => this.isActiveProject(project))
            : [];
    }

    addProject() {
        if (!this.selectedProject || !this.engagement) {
            return;
        }

        const projectId = this.selectedProject.id;
        const hadActiveProjects = this.assignedInspectionProjects.length > 0;

        if (!hadActiveProjects) {
            this.engagement.officeId = this.selectedProject.operatingUnit?.id || null;
            this.engagement.office = this.selectedProject.operatingUnit || null;
            this.syncRegionFromOffice();
        }

        const existingProject = (this.engagement.inspectionEngagementProjects || [])
            .find(project => project.projectId === this.selectedProject.id
                && !project.entityAspect.entityState.isDetached());
        if (existingProject?.entityAspect.entityState.isDeleted()) {
            existingProject.entityAspect.rejectChanges();
        }
        else if (!existingProject || existingProject.isDeleted) {
            const engagementProject = this.uow.inspectionEngagementProjects.createEntity();
            engagementProject.inspectionEngagement = this.engagement;
            engagementProject.inspectionEngagementId = this.engagement.id;
            engagementProject.project = this.selectedProject;
            engagementProject.projectId = this.selectedProject.id;
            this.engagement.inspectionEngagementProjects.push(engagementProject);
        }

        this.selectedProject = null;
        this.setAvailableProjects();
        this.syncEngagementTeamForAddedProject(projectId);
    }

    removeProject(engagementProject: model.InspectionEngagementProject) {
        if (!engagementProject) {
            return;
        }

        const projectId = engagementProject.projectId;

        this.removeProjectEntity(engagementProject);
        this.syncEngagementTeamForRemovedProject(projectId);
    }

    private removeProjectEntity(engagementProject: model.InspectionEngagementProject) {
        if (engagementProject.entityAspect.entityState.isAdded()) {
            this.uow.inspectionEngagementProjects.cancelChanges(engagementProject);
        }
        else {
            engagementProject.entityAspect.setDeleted();
        }

        if (this.assignedInspectionProjects.length === 0) {
            this.engagement.officeId = null;
            this.engagement.office = null;
            this.engagement.regionCode = null;
            this.engagement.totalEngagementHours = 0;
        }

        this.setAvailableProjects();
    }

    // ----- Workspace (AptCaw) -----

    loadAptCaws(clientId: string) {
        clearTimeout(this.aptCawSearchTimer);
        this.aptCawName = null;
        const requestVersion = ++this.aptCawRequestVersion;

        if (!clientId) {
            this.aptCaws = [];
            this.availableAptCaws = [];
            this.aptCawsCount = 0;
            return Promise.resolve();
        }

        this.aptCawsPage = 1;
        this.aptCawsLoading = true;
        return Promise.all([
            this.uow.getAptCawsCount(null, null, clientId),
            this.uow.getAptCaws(null, null, clientId, null, this.aptCawsPage, APT_CAW_PAGE_SIZE)
        ])
            .then(([count, aptCaws]) => {
                if (requestVersion === this.aptCawRequestVersion && clientId === this.engagement?.clientId) {
                    this.aptCawsCount = this.countValue(count);
                    this.aptCaws = aptCaws;
                    this.setAvailableAptCaws();
                }
            })
            .catch(reason => this.errorHandler.handleError(reason))
            .finally(() => {
                if (requestVersion === this.aptCawRequestVersion) {
                    this.aptCawsLoading = false;
                }
            });
    }

    private setAvailableAptCaws() {
        const assignedAptCawIds = new Set(
            (this.engagement?.inspectionEngagementAptCaws || [])
                .filter(aptCaw => this.isActiveAptCaw(aptCaw))
                .map(aptCaw => aptCaw.aptCawId)
        );
        this.availableAptCaws = (this.aptCaws || [])
            .filter(aptCaw => !assignedAptCawIds.has(aptCaw.id));
    }

    onAptCawsSearch(event: { term: string }) {
        this.aptCawName = (event?.term || '').trim();
        clearTimeout(this.aptCawSearchTimer);
        this.aptCawSearchTimer = setTimeout(() => this.searchAptCaws(), APT_CAW_SEARCH_TIMEOUT);
    }

    private searchAptCaws() {
        if (!this.engagement?.clientId) {
            return;
        }

        if (!this.aptCawName) {
            this.loadAptCaws(this.engagement.clientId);
            return;
        }

        this.aptCawsPage = 1;
        this.aptCawsLoading = true;
        const searchTerm = this.aptCawName;
        const requestVersion = ++this.aptCawRequestVersion;
        const clientId = this.engagement.clientId;
        Promise.all([
            this.uow.getAptCawsCount(null, searchTerm, clientId),
            this.uow.getAptCaws(null, searchTerm, clientId, null, this.aptCawsPage, APT_CAW_PAGE_SIZE)
        ])
            .then(([count, aptCaws]) => {
                if (requestVersion === this.aptCawRequestVersion
                    && searchTerm === this.aptCawName
                    && clientId === this.engagement?.clientId) {
                    this.aptCawsCount = this.countValue(count);
                    this.aptCaws = aptCaws;
                    this.setAvailableAptCaws();
                }
            })
            .catch(reason => this.errorHandler.handleError(reason))
            .finally(() => {
                if (requestVersion === this.aptCawRequestVersion) {
                    this.aptCawsLoading = false;
                }
            });
    }

    onAptCawsScrollToEnd() {
        this.fetchMoreAptCaws();
    }

    onAptCawsScroll(event: { end: number }) {
        if (!event || this.aptCawsLoading || this.aptCaws.length >= this.aptCawsCount) {
            return;
        }

        if (event.end >= this.aptCaws.length - 10) {
            this.fetchMoreAptCaws();
        }
    }

    private fetchMoreAptCaws() {
        if (this.aptCawsLoading || this.aptCaws.length >= this.aptCawsCount || !this.engagement?.clientId) {
            return;
        }

        const requestVersion = this.aptCawRequestVersion;
        const clientId = this.engagement.clientId;
        const searchTerm = this.aptCawName;
        const nextPage = this.aptCawsPage + 1;
        this.aptCawsPage = nextPage;
        this.aptCawsLoading = true;
        this.uow.getAptCaws(null, searchTerm, clientId, null, nextPage, APT_CAW_PAGE_SIZE)
            .then(result => {
                if (requestVersion === this.aptCawRequestVersion
                    && clientId === this.engagement?.clientId
                    && searchTerm === this.aptCawName) {
                    this.aptCaws = this.aptCaws.concat(result);
                    this.setAvailableAptCaws();
                }
            })
            .catch(reason => this.errorHandler.handleError(reason))
            .finally(() => {
                if (requestVersion === this.aptCawRequestVersion) {
                    this.aptCawsLoading = false;
                }
            });
    }

    private isActiveAptCaw(engagementAptCaw: model.InspectionEngagementAptCaw) {
        return !engagementAptCaw.isDeleted
            && !engagementAptCaw.entityAspect.entityState.isDeleted()
            && !engagementAptCaw.entityAspect.entityState.isDetached();
    }

    get assignedAptCaws() {
        return this.engagement && this.engagement.inspectionEngagementAptCaws
            ? this.engagement.inspectionEngagementAptCaws.filter(aptCaw => this.isActiveAptCaw(aptCaw))
            : [];
    }

    addAptCaw() {
        if (!this.selectedAptCaw || !this.engagement) {
            return;
        }

        const aptCaw = this.selectedAptCaw;
        const isFirstAptCaw = !this.assignedAptCaws.length;

        const existing = (this.engagement.inspectionEngagementAptCaws || [])
            .find(a => a.aptCawId === aptCaw.id && !a.entityAspect.entityState.isDetached());
        if (existing?.entityAspect.entityState.isDeleted()) {
            existing.entityAspect.rejectChanges();
        }
        else if (!existing || existing.isDeleted) {
            const engagementAptCaw = this.uow.inspectionEngagementAptCaws.createEntity();
            engagementAptCaw.inspectionEngagement = this.engagement;
            engagementAptCaw.inspectionEngagementId = this.engagement.id;
            engagementAptCaw.aptCaw = aptCaw;
            engagementAptCaw.aptCawId = aptCaw.id;
            this.engagement.inspectionEngagementAptCaws.push(engagementAptCaw);
        }

        if (isFirstAptCaw) {
            this.engagement.engagementType = aptCaw.engagementType;
            this.engagement.clientEntityType = aptCaw.clientEntityType;
            this.engagement.clientFiscalYearEnd = aptCaw.fiscalYearEnd;
        }

        (aptCaw.industry || '').split('|')
            .map(name => (name || '').trim())
            .filter(name => !!name)
            .forEach(name => {
                const industry = this.industries.find(i => i.name === name);
                if (industry) {
                    this.selectedIndustry = industry;
                    this.addIndustry();
                }
                else {
                    this.addIndustry(name);
                }
            });

        this.selectedAptCaw = null;
        this.setAvailableAptCaws();
    }

    removeAptCaw(engagementAptCaw: model.InspectionEngagementAptCaw) {
        if (!engagementAptCaw) {
            return;
        }

        if (engagementAptCaw.entityAspect.entityState.isAdded()) {
            this.uow.inspectionEngagementAptCaws.cancelChanges(engagementAptCaw);
        }
        else {
            engagementAptCaw.entityAspect.setDeleted();
        }

        if (!this.assignedAptCaws.length) {
            this.engagement.engagementType = null;
            this.engagement.clientEntityType = null;
            this.engagement.clientFiscalYearEnd = null;
            this.assignedIndustries.slice().forEach(industry => this.removeIndustry(industry));
        }

        this.setAvailableAptCaws();
    }

    // ----- Industries -----

    private isActiveIndustry(engagementIndustry: model.InspectionEngagementIndustry) {
        return !engagementIndustry.isDeleted
            && !engagementIndustry.entityAspect.entityState.isDeleted()
            && !engagementIndustry.entityAspect.entityState.isDetached();
    }

    get assignedIndustries() {
        return this.engagement && this.engagement.inspectionEngagementIndustries
            ? this.engagement.inspectionEngagementIndustries.filter(industry => this.isActiveIndustry(industry))
            : [];
    }

    industryLabel(engagementIndustry: model.InspectionEngagementIndustry) {
        return engagementIndustry.industry ? engagementIndustry.industry.name : engagementIndustry.industryName;
    }

    private setAvailableIndustries() {
        const assignedIndustryIds = new Set(
            this.assignedIndustries
                .filter(industry => industry.industryId != null)
                .map(industry => industry.industryId)
        );
        this.availableIndustries = (this.industries || [])
            .filter(industry => !assignedIndustryIds.has(industry.id));
    }

    addIndustry(name?: string) {
        if (!this.engagement) {
            return;
        }

        if (name) {
            const duplicate = this.assignedIndustries
                .some(i => this.industryLabel(i) === name);
            if (!duplicate) {
                const engagementIndustry = this.uow.inspectionEngagementIndustries.createEntity();
                engagementIndustry.inspectionEngagement = this.engagement;
                engagementIndustry.inspectionEngagementId = this.engagement.id;
                engagementIndustry.industryName = name;
                this.engagement.inspectionEngagementIndustries.push(engagementIndustry);
            }
        }
        else {
            if (!this.selectedIndustry) {
                return;
            }

            const industry = this.selectedIndustry;
            const existing = (this.engagement.inspectionEngagementIndustries || [])
                .find(i => i.industryId === industry.id && !i.entityAspect.entityState.isDetached());
            if (existing?.entityAspect.entityState.isDeleted()) {
                existing.entityAspect.rejectChanges();
            }
            else if (!existing || existing.isDeleted) {
                const engagementIndustry = this.uow.inspectionEngagementIndustries.createEntity();
                engagementIndustry.inspectionEngagement = this.engagement;
                engagementIndustry.inspectionEngagementId = this.engagement.id;
                engagementIndustry.industry = industry;
                engagementIndustry.industryId = industry.id;
                engagementIndustry.industryName = industry.name;
                this.engagement.inspectionEngagementIndustries.push(engagementIndustry);
            }

            this.selectedIndustry = null;
        }

        this.setAvailableIndustries();
    }

    removeIndustry(engagementIndustry: model.InspectionEngagementIndustry) {
        if (!engagementIndustry) {
            return;
        }

        if (engagementIndustry.entityAspect.entityState.isAdded()) {
            this.uow.inspectionEngagementIndustries.cancelChanges(engagementIndustry);
        }
        else {
            engagementIndustry.entityAspect.setDeleted();
        }

        this.setAvailableIndustries();
    }

    // ----- Inspection Team -----

    get assignedInspectionTeamMembers() {
        return this.engagement && this.engagement.inspectionTeamMembers
            ? this.engagement.inspectionTeamMembers.filter(m => !m.isDeleted)
            : [];
    }

    searchEmployees(searchTerm: string) {
        return this.uow.getEmployees(searchTerm)
            .then(result => {
                this.employees = result.filter(m => m.isActive);

                if (this.engagement && this.engagement.inspectionTeamMembers) {
                    this.availableInspectionEmployees = this.employees.filter(m =>
                        !m.isDeleted &&
                        m.id !== this.engagement.inspectionLeadId &&
                        !this.engagement.inspectionTeamMembers.some(n => !n.isDeleted && n.employeeId == m.id));
                }
                else {
                    this.availableInspectionEmployees = this.employees.filter(m => !m.isDeleted && m.id !== this.engagement.inspectionLeadId);
                }

                this.setAvailableEngagementEmployees();
            });
    }

    addTeamCaptainToTeamIfNecessary() {
        if (this.engagement && this.engagement.inspectionLeadId) {
            if (!this.engagement.inspectionTeamMembers.some(m => !m.isDeleted && m.employeeId == this.engagement.inspectionLeadId)) {
                const member = this.uow.inspectionEngagementTeamMembers.createEntity();
                const employee = this.employees.find(m => m.id == this.engagement.inspectionLeadId);
                member.employeeId = this.engagement.inspectionLeadId;
                if (employee) {
                    member.employee = employee;
                }
                member.inspectionEngagement = this.engagement;
                member.inspectionEngagementId = this.engagement.id;
                this.engagement.inspectionTeamMembers.push(member);
            }
        }
    }

    addInspectionTeamMember() {
        if (this.selectedInspectionTeamId) {
            if (!this.engagement.inspectionTeamMembers.some(m => !m.isDeleted && m.employeeId == this.selectedInspectionTeamId)) {
                const member = this.uow.inspectionEngagementTeamMembers.createEntity();
                const employee = this.employees.find(m => m.id == this.selectedInspectionTeamId);
                if (employee) {
                    member.employee = employee;
                    member.employeeId = employee.id;
                    member.inspectionEngagement = this.engagement;
                    member.inspectionEngagementId = this.engagement.id;
                    this.engagement.inspectionTeamMembers.push(member);
                }
                else {
                    this.uow.inspectionEngagementTeamMembers.cancelChanges(member);
                }
            }
        }

        this.selectedInspectionTeamId = null;
    }

    removeInspectionTeamMember(member: model.InspectionEngagementTeamMember) {
        const name = member.employee ? member.employee.fullName : 'this team member';
        if (confirm('Remove "' + name + '" from the inspection team?')) {
            (member.inspectionEngagementTeamMemberTopics || [])
                .filter(t => !t.isDeleted)
                .slice()
                .forEach(topicRow => this.deleteTopicRow(topicRow));

            if (member.entityAspect.entityState.isAdded()) {
                this.uow.inspectionEngagementTeamMembers.cancelChanges(member);
            }
            else {
                member.entityAspect.setDeleted();
            }
        }
    }

    availableSpecialtyReviewerTypes(reviewerTypeId) {
        return this.specialtyReviewerTypes.filter(type =>
            reviewerTypeId == type.id ||
            !this.assignedInspectionTeamMembers.some(tm => tm.specialtyReviewerTypeId == type.id));
    }

    getTeamMemberTopics(member: model.InspectionEngagementTeamMember) {
        return member && member.inspectionEngagementTeamMemberTopics
            ? member.inspectionEngagementTeamMemberTopics.filter(t => !t.isDeleted)
            : [];
    }

    addTopicRow(member: model.InspectionEngagementTeamMember) {
        const topicRow = this.uow.inspectionEngagementTeamMemberTopics.createEntity();
        topicRow.inspectionEngagementTeamMember = member;
        topicRow.inspectionEngagementTeamMemberId = member.id;
        member.inspectionEngagementTeamMemberTopics.push(topicRow);
        this.standardSelections[topicRow.id] = [];
    }

    onTopicChange(topicRow: model.InspectionEngagementTeamMemberTopic, member: model.InspectionEngagementTeamMember) {
        if (!topicRow.topicId) {
            return;
        }

        const duplicate = member.inspectionEngagementTeamMemberTopics
            .some(t => !t.isDeleted && t !== topicRow && t.topicId == topicRow.topicId);

        if (duplicate) {
            alert('That topic is already assigned to this team member.');
            topicRow.topicId = null;
        }
    }

    removeTopicRow(topicRow: model.InspectionEngagementTeamMemberTopic) {
        if (confirm('Remove this topic assignment?')) {
            this.deleteTopicRow(topicRow);
        }
    }

    private deleteTopicRow(topicRow: model.InspectionEngagementTeamMemberTopic) {
        (topicRow.inspectionEngagementTeamMemberTopicStandards || [])
            .filter(s => !s.isDeleted)
            .slice()
            .forEach(standardRow => this.removeStandardEntity(standardRow));

        if (topicRow.entityAspect.entityState.isAdded()) {
            this.uow.inspectionEngagementTeamMemberTopics.cancelChanges(topicRow);
        }
        else {
            topicRow.entityAspect.setDeleted();
        }

        delete this.standardSelections[topicRow.id];
    }

    onStandardsChange(topicRow: model.InspectionEngagementTeamMemberTopic, selectedIds: number[]) {
        const ids = selectedIds || [];
        const current = (topicRow.inspectionEngagementTeamMemberTopicStandards || []).filter(s => !s.isDeleted);

        ids.filter(id => !current.some(s => s.standardId == id))
            .forEach(id => this.addStandardEntity(topicRow, id));

        current.filter(s => !ids.some(id => id == s.standardId))
            .forEach(s => this.removeStandardEntity(s));

        this.standardSelections[topicRow.id] = ids;
    }

    private addStandardEntity(topicRow: model.InspectionEngagementTeamMemberTopic, standardId: number) {
        const standardRow = this.uow.inspectionEngagementTeamMemberTopicStandards.createEntity();
        standardRow.inspectionEngagementTeamMemberTopic = topicRow;
        standardRow.inspectionEngagementTeamMemberTopicId = topicRow.id;
        standardRow.standardId = standardId;
        const standard = this.standards.find(m => m.id == standardId);
        if (standard) {
            standardRow.standard = standard;
        }
        topicRow.inspectionEngagementTeamMemberTopicStandards.push(standardRow);
    }

    private removeStandardEntity(standardRow: model.InspectionEngagementTeamMemberTopicStandard) {
        if (standardRow.entityAspect.entityState.isAdded()) {
            this.uow.inspectionEngagementTeamMemberTopicStandards.cancelChanges(standardRow);
        }
        else {
            standardRow.entityAspect.setDeleted();
        }
    }

    private hasIncompleteTopicRows(): boolean {
        return this.assignedInspectionTeamMembers.some(member =>
            this.getTeamMemberTopics(member).some(topicRow => !topicRow.topicId));
    }

    // ----- Engagement Team -----

    private isActiveEngagementTeamMember(member: model.InspectionEngagementEngagementTeamMember) {
        return member
            && !member.isDeleted
            && !member.entityAspect.entityState.isDeleted()
            && !member.entityAspect.entityState.isDetached();
    }

    get assignedEngagementTeamMembers() {
        return this.engagement && this.engagement.inspectionEngagementEngagementTeamMembers
            ? this.engagement.inspectionEngagementEngagementTeamMembers.filter(m => this.isActiveEngagementTeamMember(m))
            : [];
    }

    setAvailableEngagementEmployees() {
        const assigned = this.assignedEngagementTeamMembers;
        this.availableEngagementEmployees = this.employees.filter(m =>
            !m.isDeleted && !assigned.some(n => n.employeeId == m.id));
    }

    async addEngagementTeamMember() {
        const employeeId = this.selectedEngagementTeamId;
        this.selectedEngagementTeamId = null;

        if (!employeeId || !this.engagement) {
            return;
        }

        if (this.assignedEngagementTeamMembers.some(m => m.employeeId == employeeId)) {
            return;
        }

        const employee = this.employees.find(m => m.id == employeeId);
        if (!employee) {
            return;
        }

        const member = this.createEngagementTeamMember(employee.id, employee, null, null);
        if (!member) {
            return;
        }

        this.setAvailableEngagementEmployees();

        const syncVersion = this.engagementTeamSyncVersion;

        await this.uow.getTotalEmployeeProjectHours(this.currentProjectIds(), employeeId)
            .then(result => {
                if (syncVersion !== this.engagementTeamSyncVersion
                    || !this.isActiveEngagementTeamMember(member)) {
                    return;
                }

                member.hours = this.totalEmployeeProjectHoursValue(result);
            })
            .catch(reason => this.errorHandler.handleError(reason));
    }

    removeEngagementTeamMember(member: model.InspectionEngagementEngagementTeamMember) {
        const name = member.employee ? member.employee.fullName : 'this team member';
        if (!confirm('Remove "' + name + '" from the engagement team?')) {
            return;
        }

        this.deleteEngagementTeamMember(member);
        this.setAvailableEngagementEmployees();
    }

    private deleteEngagementTeamMember(member: model.InspectionEngagementEngagementTeamMember) {
        if (member.entityAspect.entityState.isAdded()) {
            this.uow.inspectionEngagementEngagementTeamMembers.cancelChanges(member);
        }
        else {
            member.entityAspect.setDeleted();
        }
    }

    private clearEngagementTeam() {
        this.engagementTeamSyncVersion++;
        this.assignedEngagementTeamMembers
            .slice()
            .forEach(member => this.deleteEngagementTeamMember(member));
        if (this.engagement) {
            this.engagement.totalEngagementHours = 0;
        }
        this.setAvailableEngagementEmployees();
    }

    private createEngagementTeamMember(employeeId: string, employee: model.Employee, hours: number, roleId: number) {
        if (!this.engagement || !employeeId) {
            return null;
        }

        if (this.assignedEngagementTeamMembers.some(m => m.employeeId == employeeId)) {
            return null;
        }

        const member = this.uow.inspectionEngagementEngagementTeamMembers.createEntity();
        member.employeeId = employeeId;
        if (employee) {
            member.employee = employee;
        }
        member.hours = hours;
        member.inspectionTeamRoleId = roleId;
        member.inspectionEngagement = this.engagement;
        member.inspectionEngagementId = this.engagement.id;

        if (!this.engagement.inspectionEngagementEngagementTeamMembers.some(m => m === member)) {
            this.engagement.inspectionEngagementEngagementTeamMembers.push(member);
        }

        return member;
    }

    private currentProjectIds(): string[] {
        return this.assignedInspectionProjects.map(project => project.projectId);
    }

    private totalEmployeeProjectHoursValue(result: number | number[] | null): number | null {
        const value = Array.isArray(result) ? result[0] : result;
        return value == null ? null : value;
    }

    private roundHours(value: number): number {
        return Math.round(value * 100) / 100;
    }

    private syncEngagementTeamForAddedProject(projectId: string) {
        return this.queueEngagementTeamSync(() => {
            if (!this.assignedInspectionProjects.some(p => p.projectId == projectId)) {
                return Promise.resolve();
            }

            return this.uow.getEmployeeProjectHours(projectId)
                .then(result => {
                    (result || []).forEach(eph => {
                        const role = this.teamMemberRoles.find(r => r.name && r.name.toLowerCase() == 'other');
                        this.createEngagementTeamMember(
                            eph.employeeId,
                            eph.employee || this.employees.find(m => m.id == eph.employeeId),
                            null,
                            role ? role.id : null);
                    });
                })
                .then(() => this.recalculateEngagementTeamHours())
                .then(() => this.setAvailableEngagementEmployees());
        });
    }

    private recalculateEngagementTeamHours() {
        const members = this.assignedEngagementTeamMembers;
        if (!members.length) {
            return Promise.resolve();
        }

        const projectIds = this.currentProjectIds();

        return Promise.all(members.map(member =>
            this.uow.getTotalEmployeeProjectHours(projectIds, member.employeeId)
                .then(result => {
                    if (this.isActiveEngagementTeamMember(member)) {
                        member.hours = this.totalEmployeeProjectHoursValue(result);
                    }
                })))
            .then(() => {
                const total = this.assignedEngagementTeamMembers
                    .reduce((sum, member) => sum + (member.hours || 0), 0);
                this.engagement.totalEngagementHours = this.roundHours(total);
            });
    }

    private syncEngagementTeamForRemovedProject(projectId: string) {
        return this.queueEngagementTeamSync(() => {
            if (this.assignedInspectionProjects.some(p => p.projectId == projectId)) {
                return Promise.resolve();
            }

            return this.uow.getEmployeeProjectHours(projectId)
                .then(result => {
                    (result || []).forEach(eph => {
                        const member = this.assignedEngagementTeamMembers.find(m => m.employeeId == eph.employeeId);
                        if (!member) {
                            return;
                        }

                        member.hours = (member.hours || 0) - (eph.hours || 0);
                        this.engagement.totalEngagementHours = (this.engagement.totalEngagementHours || 0) - (eph.hours || 0);

                        if (member.hours <= 0) {
                            this.deleteEngagementTeamMember(member);
                        }
                    });

                    if (this.engagement.totalEngagementHours < 0) {
                        this.engagement.totalEngagementHours = 0;
                    }
                    this.engagement.totalEngagementHours = this.roundHours(this.engagement.totalEngagementHours || 0);
                    this.setAvailableEngagementEmployees();
                });
        });
    }

    // Engagement team syncs mutate shared state (hours, totals, membership) from async
    // responses, so they are serialized and abandoned when the client changes underneath them.
    private queueEngagementTeamSync(work: () => Promise<any>) {
        const syncVersion = this.engagementTeamSyncVersion;

        this.engagementTeamSyncChain = this.engagementTeamSyncChain
            .then(() => {
                if (syncVersion !== this.engagementTeamSyncVersion || !this.engagement) {
                    return null;
                }

                this.isBusy.set(LOADING_KEY, true, 'Loading project hours');

                return work()
                    .catch(reason => this.errorHandler.handleError(reason))
                    .finally(() => this.isBusy.set(LOADING_KEY, false));
            })
            .catch(reason => this.errorHandler.handleError(reason));

        return this.engagementTeamSyncChain;
    }

    saveChanges() {
        this.addTeamCaptainToTeamIfNecessary();

        if (this.hasIncompleteTopicRows()) {
            alert('Please select a topic for every topic assignment, or remove the empty rows.');
            return;
        }

        this.isBusy.set(SAVING_KEY, true, 'Saving inspection engagement');

        this.uow.commit()
            .then(() => this.uow.syncInspectionEngagementQuestions(this.engagement.id))
            .then(() => {
                this.goHome();
            })
            .catch(reason => {
                this.errorHandler.handleError(reason);
            })
            .finally(() => {
                this.isBusy.set(SAVING_KEY, false);
            });
    }

    goHome() {
        this.router.navigate(['../inspection-engagements'], { relativeTo: this.activeRoute });
    }
}
